import * as Label from "@radix-ui/react-label"
import * as React from "react"

import clsxm from "@/lib/clsxm"

/** TODO: Design "combined" input variant */

type TextFieldProps = {
  /** Validation error state */
  error?: string
  /** id for input and label to htmlFor */
  id: string
  /** Make the input the full width of its container */
  fullwidth?: boolean
  /** Additional helper text */
  helperText?: string
  /** Label for the text field */
  label?: string
  /** The type of input */
  type: string
  /** Classname for the div container wrapper */
  wrapperClassName?: string
  phoneNumbers?: boolean
  onChange?: React.ChangeEventHandler<HTMLInputElement>
  variant: `outline` | `minimal`
} & React.ComponentPropsWithRef<`input`>

const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      autoComplete,
      autoFocus,
      children,
      className,
      disabled = false,
      error,
      fullwidth,
      helperText,
      id,
      label,
      onChange,
      onBlur,
      name,
      /** Required for tailwind floating label styling */
      placeholder = ` `,
      required = false,
      type,
      wrapperClassName,
      variant = `outline`,
      phoneNumbers,
      ...rest
    },
    ref
  ) => {
    const [value, setValue] = React.useState<number | string>()
    const inputClasses = clsxm(
      `peer block w-full appearance-none rounded-lg bg-white text-sm text-gray-900  focus:outline-none focus:ring-0`,
      [
        variant === `outline` && [
          `border border-gray-300 focus:border-gray-600 px-3 pb-2.5 pt-5`,
        ],
      ],
      [variant === `minimal` && [`px-3 py-3.5 border-none`]],
      phoneNumbers && `pl-10`,
      className
    )

    const labelClasses = clsxm(
      `absolute top-4 left-3 z-10 origin-[0] -translate-y-3 scale-75 transform text-sm text-gray-500 duration-300 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:-translate-y-3 peer-focus:scale-75 peer-focus:text-gray-600 dark:text-gray-400 peer-focus:dark:text-gray-500`
    )

    function handlePhoneNumbers(e: React.ChangeEvent<HTMLInputElement>) {
      const result = e.target.value.replace(/\D/g, ``)
      setValue(result)
    }

    return (
      <div
        className={clsxm([
          `relative flex flex-1 items-center justify-center`,
          wrapperClassName,
        ])}
      >
        <input
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          className={inputClasses}
          disabled={disabled}
          id={id}
          placeholder={placeholder}
          ref={ref}
          required={required}
          type={type}
          onChange={(e) => {
            onChange && onChange(e)
            phoneNumbers && handlePhoneNumbers(e)
          }}
          onBlur={onBlur}
          name={name}
          value={value}
          {...rest}
        ></input>
        {children}
        {variant !== `minimal` && (
          <Label.Root className={labelClasses} id={`${id}-label`} htmlFor={id}>
            {label}
          </Label.Root>
        )}
        {phoneNumbers && (
          <div
            className={clsxm(
              `absolute bottom-0 left-3 pb-3 text-sm leading-tight`,
              `peer-placeholder-shown:hidden`,
              `peer-focus:block`
            )}
          >
            +64
          </div>
        )}
        <span>{helperText}</span>
        {error && <span className={`text-sm text-red-500`}>{error}</span>}
      </div>
    )
  }
)

export default TextField
